/**
 * Ticket-sheet print route — a full-page preview of the A4 job check-in sheet
 * for one work item, printed via the system dialog (Ctrl/Cmd + P). Mirrors
 * CheckInPrintSlipPage's print pattern; unlike that page we do NOT auto-print —
 * the on-screen render is a preview first.
 *
 * When the work item is a check-in session whose device inspection(s) are
 * complete, the sheet prints the captured make/serial + Yes/No checks + notes,
 * one A4 page per device. Otherwise (plain ticket, or inspection still in
 * progress) it prints the blank hand-fill sheet.
 *
 * Toolbar (hidden on print) exposes the two "template" controls:
 *   • Variant  — Retail (pricing boxes) / Monthly (billed on monthly invoice).
 *                Defaults from the customer's monthly-invoice flag; overridable.
 *   • Data     — Live ticket / Sample (built-in example fixture).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { WorkItem } from '@techgitter/contracts';

import { useCustomerHasMonthlyInvoiceSchedule } from '../hooks/useCustomerHasMonthlyInvoiceSchedule';
import { useLatestInvoicePrefix } from '../hooks/useLatestInvoicePrefix';
import { useCheckInSession } from '../hooks/useCheckInSession';
import { TicketSheet } from '../components/tickets/TicketSheet/TicketSheet';
import {
  buildSampleTicketSheetData,
  isInspectionComplete,
  mapDraftJobToCapturedInspection,
  mapWorkItemToTicketSheetData,
  type CapturedInspection,
  type TicketSheetData,
  type TicketSheetVariant,
} from '../components/tickets/TicketSheet/ticketSheetData';
import './TicketSheetPrintPage.css';

export interface TicketSheetPrintPageProps {
  workItem: WorkItem;
  onBack: () => void;
}

type DataSource = 'live' | 'sample';

export const TicketSheetPrintPage: React.FC<TicketSheetPrintPageProps> = ({ workItem, onBack }) => {
  const { hasMonthlyInvoiceSchedule } = useCustomerHasMonthlyInvoiceSchedule(workItem.customerId);
  const { prefix: invoiceIdPrefix } = useLatestInvoicePrefix();

  // Fetch the full session whenever the work item is linked to a check-in —
  // check-in rows AND jobs converted from a check-in both carry
  // checkInSessionGuid (the presence of the guid, not the work-item type,
  // decides the fetch). Items with no linked session → no fetch → blank sheet.
  const sessionId = workItem.checkInSessionGuid ?? null;
  const { session, isLoading: isSessionLoading } = useCheckInSession(sessionId);

  // Completed device inspections → one captured block (one printed page) each.
  // Scope to the device(s) linked to THIS work item: the backend matches a
  // job's draft jobs to its RS ticket id and exposes them as checkInDraftJobs,
  // so a multi-device session only prints this job's device(s). Fall back to
  // every device when the link summary is absent (e.g. a check-in row).
  const capturedDevices = useMemo<CapturedInspection[]>(() => {
    const draftJobs = session?.draftJobs ?? [];
    if (draftJobs.length === 0) return [];
    const linkedIds = new Set((workItem.checkInDraftJobs ?? []).map(dj => dj.id));
    const scoped = linkedIds.size > 0 ? draftJobs.filter(j => linkedIds.has(j.id)) : draftJobs;
    return scoped.filter(isInspectionComplete).map(mapDraftJobToCapturedInspection);
  }, [session, workItem.checkInDraftJobs]);

  // Initial guess from the business name avoids a flash before the flag loads;
  // the monthly-invoice flag (below) is authoritative once resolved.
  const [variant, setVariant] = useState<TicketSheetVariant>(() =>
    workItem.customerBusinessName ? 'monthly' : 'retail',
  );
  const [dataSource, setDataSource] = useState<DataSource>('live');
  // Once the technician picks a variant, stop auto-syncing it from the flag.
  const userChoseVariant = useRef(false);

  useEffect(() => {
    if (userChoseVariant.current || hasMonthlyInvoiceSchedule == null) return;
    setVariant(hasMonthlyInvoiceSchedule ? 'monthly' : 'retail');
  }, [hasMonthlyInvoiceSchedule]);

  const liveData = useMemo(() => mapWorkItemToTicketSheetData(workItem), [workItem]);
  const baseData = dataSource === 'live' ? liveData : buildSampleTicketSheetData(variant);
  const data = invoiceIdPrefix ? { ...baseData, invoiceIdPrefix } : baseData;

  // Only the live view prints captured check-in data; Sample stays a blank sheet.
  const showCaptured = dataSource === 'live' && capturedDevices.length > 0;
  const waitingForSession = dataSource === 'live' && sessionId != null && isSessionLoading;

  // Per-device header: prefer the device's own RS ticket number + service
  // summary over the session-level work-item header. The captured number is
  // blank for draft jobs recorded before the ticket-number column was
  // provisioned, so fall back to the work item's own number — never to the
  // opaque RS ticket id, which misprints as the ticket number.
  const perDeviceData = (captured: CapturedInspection): TicketSheetData => ({
    ...data,
    ticketNumber: captured.ticketNumber || data.ticketNumber,
    subject: captured.serviceSummary || data.subject,
  });

  return (
    <>
      <PrintStyles />

      <Box
        className="ticket-sheet-toolbar"
        sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>Back</Button>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={variant}
          onChange={(_e, v: TicketSheetVariant | null) => {
            if (!v) return;
            userChoseVariant.current = true;
            setVariant(v);
          }}
          aria-label="Sheet variant"
        >
          <ToggleButton value="retail">Retail</ToggleButton>
          <ToggleButton value="monthly">Monthly invoice</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={dataSource}
          onChange={(_e, v: DataSource | null) => v && setDataSource(v)}
          aria-label="Data source"
        >
          <ToggleButton value="live">Live ticket</ToggleButton>
          <ToggleButton value="sample">Sample</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print
        </Button>
      </Box>

      <div className="ticket-sheet-page">
        {waitingForSession ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 6, color: 'text.secondary' }}>
            <CircularProgress size={20} />
            Loading check-in details…
          </Box>
        ) : showCaptured ? (
          capturedDevices.map((captured, i) => (
            <div
              key={captured.ticketNumber || `${captured.deviceType}-${i}`}
              className="ticket-sheet-page__sheet"
            >
              <TicketSheet data={perDeviceData(captured)} variant={variant} captured={captured} />
            </div>
          ))
        ) : (
          <div className="ticket-sheet-page__sheet">
            <TicketSheet data={data} variant={variant} />
          </div>
        )}
      </div>
    </>
  );
};

/**
 * Print-only styles: hide the toolbar + app chrome, reset margins and drop the
 * on-screen page surround so only the A4 sheet prints.
 */
const PrintStyles: React.FC = () => (
  <style
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{
      __html: `
        @page { size: A4; margin: 10mm; }
        @media print {
          body { margin: 0; background: #fff; }
          .ticket-sheet-toolbar { display: none !important; }
          .main-layout > header, .main-layout > nav { display: none !important; }
          .ticket-sheet-page { padding: 0 !important; background: #fff !important; }
        }
      `,
    }}
  />
);
